
import {
  Badge,
  Box,
  Button,
  Container,
  HStack,
  Image,
  Progress,
  Radio,
  RadioGroup,
  Stat,
  StatArrow,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
  VStack,
} from "@chakra-ui/react";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { server } from "../index";
import Chart from "./Chart";
import ErrorComponent from "./ErrorComponent";
import Loader from "./Loader";

// Displays detailed information about a cryptocurrency, including its historical price chart and other relevant data.
const CoinDetails = () => {
  const params = useParams();// useParams to get the coin ID from the URL.
  const [coin, setCoin] = useState({}); // State to store the coin data.
  const [loading, setLoading] = useState(true); // State to track loading status.
  const [error, setError] = useState(false); // State to track error status.
  const [currency, setCurrency] = useState("inr"); // State to track the selected currency.
  const [days, setDays] = useState("24h"); // State to track the selected time frame.
  const [chartArray, setChartArray] = useState([]); // State to store the historical price chart data.

  // Define the currency symbol based on the selected currency.
  const currencySymbol =
    currency === "inr" ? "₹" : currency === "eur" ? "€" : "$";

    // Array of time frame buttons.
  const btns = ["24h", "7d", "14d", "30d", "60d", "200d", "1y", "max"];

  // Function to switch between different time frames for the historical price chart.
  const switchChartStats = (key) => {
    switch (key) {
      case "24h":
        setDays("24h");
        setLoading(true);
        break;
      case "7d":
        setDays("7d");
        setLoading(true);
        break;
      case "14d":
        setDays("14d");
        setLoading(true);
        break;
      case "30d":
        setDays("30d");
        setLoading(true);
        break;
      case "60d":
        setDays("60d");
        setLoading(true);
        break;
      case "200d":
        setDays("200d");
        setLoading(true);
        break;
      case "1y":
        setDays("365d");
        setLoading(true);
        break;
      case "max":
        setDays("max");
        setLoading(true);
        break;

      default:
        setDays("24h");
        setLoading(true);
        break;
    }
  };

  // Fetch coin data and historical price chart data based on the selected currency and time frame.     
  useEffect(() => {
    const fetchCoin = async () => {
      try {
        const { data } = await axios.get(`${server}/coins/${params.id}`);

        const { data: chartData } = await axios.get(
          `${server}/coins/${params.id}/market_chart?vs_currency=${currency}&days=${days}`
        );
        setCoin(data); // Set the coin data.
        setChartArray(chartData.prices); // Set the historical price chart data.
        setLoading(false);  // Set loading to false.
      } catch (error) { // Handle errors.
        setError(true); // Set error status.
        setLoading(false);  // Set loading to false.
      }
    };
    fetchCoin(); // Call the fetchCoin function when the coin ID, currency, or time frame changes.
  }, [params.id, currency, days]); // Fetch data when the coin ID, currency, or time frame changes.

  if (error) return <ErrorComponent message={"Error While Fetching Coin"} />;

  // Render the CoinDetails component.
  return (
    <Container maxW={"container.xl"}>
      {loading ? (
        <Loader />
      ) : (
        <>
        {/*  Display the historical price chart, currency selector, and time frame selector. */}
          <Box width={"full"} borderWidth={1}>
            <Chart arr={chartArray} currency={currencySymbol} days={days} />
          </Box>
          {/* Display the time frame selector buttons. */}
          <HStack p="4" overflowX={"auto"}>
            {btns.map((i) => (
              <Button
                disabled={days === i}
                key={i}
                onClick={() => switchChartStats(i)}
              >
                {i}
              </Button>
            ))}
          </HStack>
      {/*  Display the currency selector. */}
          <RadioGroup value={currency} onChange={setCurrency} p={"8"}>
            <HStack spacing={"4"}>
              <Radio value={"inr"}>INR</Radio>
              <Radio value={"usd"}>USD</Radio>
              <Radio value={"eur"}>EUR</Radio>
            </HStack>
          </RadioGroup>
      {/*  Display the coin details. */}
          <VStack spacing={"4"} p="16" alignItems={"flex-start"}>
            <Text fontSize={"small"} alignSelf="center" opacity={0.7}>
              Last Updated On{" "}
              {Date(coin.market_data.last_updated).split("G")[0]}
            </Text>
      {/*  Display the coin image, name, current price, price change percentage, market cap rank, and other relevant data. */}
            <Image
              src={coin.image.large}
              w={"16"}
              h={"16"}
              objectFit={"contain"}
            />
      {/* Display the coin image, name, current price, price change percentage, market cap rank, and other relevant data. */}
            <Stat>
             
              <StatLabel>{coin.name}</StatLabel>
              <StatNumber>
                {currencySymbol}
                {coin.market_data.current_price[currency]}
              </StatNumber>
              {/*  Display the price change percentage and market cap rank. */}
              <StatHelpText>
                <StatArrow
                  type={
                    coin.market_data.price_change_percentage_24h > 0
                      ? "increase"
                      : "decrease"
                  }
                />
                {coin.market_data.price_change_percentage_24h}%
              </StatHelpText>
            </Stat>
{/*  Display the market cap rank and 24-hour price range. */}
            <Badge
              fontSize={"2xl"}
              bgColor={"blackAlpha.800"}
              color={"white"}
            >{`#${coin.market_cap_rank}`}</Badge>
 {/* CustomBar component to display the 24-hour price range. */}
            <CustomBar
              high={`${currencySymbol}${coin.market_data.high_24h[currency]}`}
              low={`${currencySymbol}${coin.market_data.low_24h[currency]}`}
            />
{/*  Display the maximum supply, circulating supply, market cap, all-time low, and all-time high. */}
            <Box w={"full"} p="4">
              <Item title={"Max Supply"} value={coin.market_data.max_supply} />
              <Item
                title={"Circulating Supply"}
                value={coin.market_data.circulating_supply}
              />
              <Item
                title={"Market Cap"}
                value={`${currencySymbol}${coin.market_data.market_cap[currency]}`}
              />
              <Item
                title={"All Time Low"}
                value={`${currencySymbol}${coin.market_data.atl[currency]}`}
              />
              <Item
                title={"All Time High"}
                value={`${currencySymbol}${coin.market_data.ath[currency]}`}
              />
            </Box>
          </VStack>
        </>
      )}
    </Container>
  );
};
// Item component to display a title and value pair.
const Item = ({ title, value }) => (
  <HStack justifyContent={"space-between"} w={"full"} my={"4"}>
    <Text fontFamily={"Bebas Neue"} letterSpacing={"widest"}>
      {title}
    </Text>
    <Text>{value}</Text>
  </HStack>
);

// CustomBar component to display the 24-hour price range.
const CustomBar = ({ high, low }) => (
  <VStack w={"full"}>
    <Progress value={50} colorScheme={"teal"} w={"full"} />
    <HStack justifyContent={"space-between"} w={"full"}>
      <Badge children={low} colorScheme={"red"} />
      <Text fontSize={"sm"}>24H Range</Text>
      <Badge children={high} colorScheme={"green"} />
    </HStack>
  </VStack>
);

export default CoinDetails;

/*
The CoinDetails component is a detailed view for a specific cryptocurrency. 
It fetches and displays data such as the current price, market cap, and historical price chart, along with interactive elements like currency and time frame selectors. 
The Item and CustomBar sub-components are used to structure and display specific pieces of information.
*/